import '~/modules/label-perf/styles/label-perf-override.css';

import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

const customScrollClassName = 'custom-scroll';

export interface QuickListProps {
  className?: string;
  values: (string | JSX.Element)[];
}

function textContent(elem: JSX.Element | string): string {
  if (!elem) {
    return '';
  }

  if (typeof elem === 'string') {
    return elem;
  }

  const children = (
    elem.props as { children: JSX.Element | JSX.Element[] | string }
  )?.children;

  if (children instanceof Array) {
    return children.map(textContent).join('');
  }

  return textContent(children);
}

function QuickList(props: QuickListProps) {
  const { t } = useTranslation('label-perf');
  const [search, setSearch] = useState<string>('');
  const [values, setValues] = useState<(string | JSX.Element)[]>(props.values);

  useEffect(() => {
    setValues(
      props.values.filter((value) => {
        const txt: string =
          typeof value === 'string' ? value : textContent(value);

        return txt.toLowerCase().includes(search.toLowerCase());
      }),
    );
  }, [search, props.values]);

  return (
    <div className={cn('bg-white', props.className)}>
      <div className="relative flex items-center">
        <Icon
          className={cn('absolute left-md w-[1em] text-white')}
          type="search"
          size="sm"
        />
        <input
          className="w-full rounded-sm border p-sm pl-2xl text-body-sm bg-level-05 border-emphasis-subtle focus:outline-none"
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Type to check a value"
          type="text"
          value={search}
        />
        {search ? (
          <button
            className={cn('absolute right-sm text-subtle')}
            onClick={() => setSearch('')}
          >
            <Icon className="w-[1em]" type="xmark" />
          </button>
        ) : null}
      </div>
      {values.length === 0 && (
        <div className="mt-xs rounded-sm border px-md py-sm text-body-sm bg-level-01 border-emphasis-subtle text-subtle">
          {t('noData.empty')}
        </div>
      )}
      {values.length > 0 && (
        <ul
          className={cn(
            'mt-xs h-[11em] overflow-auto rounded-sm border text-body-sm border-emphasis-subtle',
            customScrollClassName,
          )}
        >
          {values.map((value, i) => {
            const text = textContent(value);

            return (
              <li
                className="px-md py-sm bg-level-01 hover:bg-level-03"
                key={i}
                data-testid={`quicklist-item-${text}`}
              >
                {value}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

QuickList.displayName = 'QuickList';

export { QuickList };
